/**
 * Calendar periods on business-local days.
 *
 * A day is a Date at UTC midnight whose UTC fields are the local calendar date (the convention of
 * localize_utc_date). Everything here is pure, so the server load, the components and the tests
 * compute the same ranges.
 */

export const CALENDAR_VIEWS = ['list', 'day', 'week', 'month', 'year'];

/** @param {string | null | undefined} value - YYYY-MM-DD */
export function parse_day(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? null : date;
}

/** @param {Date} day */
export function day_key(day) {
  return day.toISOString().slice(0, 10);
}

/** @param {Date} day @param {number} days */
export function add_days(day, days) {
  const next = new Date(day);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

/** First day of the week containing `day`; week_start 1 = Monday, 0 = Sunday */
export function start_of_week(day, week_start = 1) {
  const offset = (day.getUTCDay() - week_start + 7) % 7;
  return add_days(day, -offset);
}

export function start_of_month(day) {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1));
}

export function end_of_month(day) {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() + 1, 0));
}

/**
 * The days a view shows around its anchor day, both inclusive.
 * @param {'day'|'week'|'month'|'year'} view
 * @param {Date} anchor
 * @param {number} [week_start]
 * @returns {{ from: Date, to: Date }}
 */
export function period_range(view, anchor, week_start = 1) {
  switch (view) {
    case 'day':
      return { from: anchor, to: anchor };
    case 'week': {
      const from = start_of_week(anchor, week_start);
      return { from, to: add_days(from, 6) };
    }
    case 'year':
      return {
        from: new Date(Date.UTC(anchor.getUTCFullYear(), 0, 1)),
        to: new Date(Date.UTC(anchor.getUTCFullYear(), 11, 31)),
      };
    case 'month':
    default:
      return { from: start_of_month(anchor), to: end_of_month(anchor) };
  }
}

/**
 * The anchor one period earlier (-1) or later (+1). Months keep the first of the month, so
 * moving from 31 January never skips February.
 */
export function shift_anchor(view, anchor, direction) {
  switch (view) {
    case 'day':
      return add_days(anchor, direction);
    case 'week':
      return add_days(anchor, 7 * direction);
    case 'year':
      return new Date(Date.UTC(anchor.getUTCFullYear() + direction, 0, 1));
    case 'month':
    default:
      return new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + direction, 1));
  }
}

/** ISO 8601 week number */
export function iso_week(day) {
  const thursday = add_days(day, 3 - ((day.getUTCDay() + 6) % 7));
  const first_thursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((thursday - first_thursday) / 86400000 - 3 + ((first_thursday.getUTCDay() + 6) % 7)) / 7);
}

/**
 * Local days an event covers, from its stored UTC times.
 * @param {{ starts_at?: string, ends_at?: string }} event
 * @param {(date: Date) => Date} localize - turns a real instant into a business-local date
 * @returns {{ first: string, last: string } | null} YYYY-MM-DD
 */
export function event_days(event, localize) {
  if (!event?.starts_at) return null;
  const starts = new Date(event.starts_at);
  if (Number.isNaN(starts.getTime())) return null;
  const ends = event.ends_at ? new Date(event.ends_at) : starts;
  const first = day_key(localize(starts));
  const last = day_key(localize(Number.isNaN(ends.getTime()) || ends < starts ? starts : ends));
  return { first, last };
}

/** Whether an event covers the local day `key` (YYYY-MM-DD) */
export function event_covers_day(days, key) {
  return !!days && days.first <= key && days.last >= key;
}
